import type { ComponentProps } from 'react';

import { cn } from '@platform/ui/lib/utils';

/*
 * A real <table>: screen readers announce row and column position from the
 * markup, which ARIA on a grid of divs does not reproduce reliably (docs/10 §4,
 * Table). The header is 11px uppercase on the ground; rows are 52px with a 1px
 * line; a numeric column and its header are right-aligned and tabular.
 */

export function Table({ className, ...props }: ComponentProps<'table'>) {
    // Narrow viewports scroll the table, not the page: a scrolling body would take
    // the navigation off screen with it. The shade on an edge says there is more.
    return (
        <div data-slot="table-container" className="relative w-full overflow-x-auto scroll-shade-x">
            <table data-slot="table" className={cn('w-full border-collapse text-13', className)} {...props} />
        </div>
    );
}

export function TableHeader({ className, ...props }: ComponentProps<'thead'>) {
    return <thead data-slot="table-header" className={cn('bg-background', className)} {...props} />;
}

export function TableBody({ className, ...props }: ComponentProps<'tbody'>) {
    return <tbody data-slot="table-body" className={cn('[&_tr:last-child]:border-b-0', className)} {...props} />;
}

export function TableRow({ className, ...props }: ComponentProps<'tr'>) {
    return <tr data-slot="table-row" className={cn('border-b border-border', className)} {...props} />;
}

export function TableHead({ className, scope = 'col', ...props }: ComponentProps<'th'>) {
    return (
        <th
            data-slot="table-head"
            scope={scope}
            className={cn(
                'h-10 px-4 text-start align-middle text-11 font-semibold tracking-label whitespace-nowrap text-muted-foreground uppercase',
                className,
            )}
            {...props}
        />
    );
}

export function TableCell({ className, ...props }: ComponentProps<'td'>) {
    return <td data-slot="table-cell" className={cn('h-13 px-4 py-3 align-middle text-foreground', className)} {...props} />;
}
